import React, { ReactElement, useEffect } from 'react';
import { DetailsList, DetailsListLayoutMode, SelectionMode, Stack, Text } from '@fluentui/react';
import { useQuery } from '@tanstack/react-query';
import { ApiCalls } from '../../../../api/api';
import { ActivityForEditing } from './types/ActivityTypes';
import { useActivityState } from './hooks/useActivityState';
import { useActivityMutations, ACTIVITIES_QUERY_KEY } from './hooks/useActivityMutations';
import { ActivityTableRow } from './components/ActivityTableRow';
import { ActivityTableHeader } from './components/ActivityTableHeader';
import { ACTIVITY_TABLE_COLUMNS, TABLE_STYLES } from './constants/tableConfig';

export const ApproverTableConfig: React.FC = () => {
    const {
        editingItems,
        errorMessage,
        setErrorMessage,
        showSuccessFeedback,
        setItemLoadingState,
        addRecord,
        updateEditingItem,
        removeNewItems,
        deleteLocalRecord,
        syncWithActivities,
    } = useActivityState();

    // Query to fetch activities
    const { data: activities = [], isLoading } = useQuery({
        queryKey: ACTIVITIES_QUERY_KEY,
        queryFn: () => ApiCalls.configurationActivitiesList().then(res => res.data),
    });

    // Initialize mutations
    const { createActivityMutation, updateActivityMutation, deleteActivityMutation } = useActivityMutations({
        onSetItemLoadingState: setItemLoadingState,
        onShowSuccessFeedback: showSuccessFeedback,
        onSetErrorMessage: setErrorMessage,
        onRemoveNewItems: removeNewItems,
    });

    const saveRecord = async (activity: ActivityForEditing) => {
        if (activity.isNew) {
            // Create new activity
            createActivityMutation.mutate({
                activity: {
                    name: activity.name,
                    rate: activity.rate,
                    rateCurrencyCode: activity.rateCurrencyCode,
                },
                tempId: activity.tempId!,
            });
        } else {
            // Update existing activity
            updateActivityMutation.mutate({
                id: activity.id!,
                activity: {
                    id: activity.id,
                    name: activity.name,
                    rate: activity.rate,
                    rateCurrencyCode: activity.rateCurrencyCode,
                },
            });
        }
    };

    const deleteRecord = async (activity: ActivityForEditing) => {
        if (activity.isNew) {
            // Remove from local state only
            deleteLocalRecord(activity.tempId!);
        } else {
            // Delete from server
            deleteActivityMutation.mutate(activity.id!);
        }
    };

    const onRenderColumn = (item: ActivityForEditing, _index: number, column: any): ReactElement => {
        return <ActivityTableRow item={item} column={column} onUpdateItem={updateEditingItem} onSaveRecord={saveRecord} onDeleteRecord={deleteRecord} />;
    };

    // Sync editing items when activities change
    useEffect(() => {
        syncWithActivities(activities);
    }, [activities, syncWithActivities]);

    if (isLoading) {
        return <Text>Loading activities...</Text>;
    }

    return (
        <Stack tokens={{ padding: 10, childrenGap: 10, maxWidth: 800 }}>
            <ActivityTableHeader onAddRecord={addRecord} errorMessage={errorMessage} onDismissError={() => setErrorMessage(null)} />
            <DetailsList
                items={editingItems}
                columns={ACTIVITY_TABLE_COLUMNS}
                selectionMode={SelectionMode.none}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                onRenderItemColumn={onRenderColumn}
                compact
                styles={TABLE_STYLES}
            />
        </Stack>
    );
};
